import { Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../../utils/cn.ts"
import { getFavorites } from "../api/global-map.api.ts"
import { GLOBAL_MAP_SURFACE_CLASS } from "../global-map-surface.ts"
import { MapIllustration } from "../MapIllustration.tsx"
import { globalMapStatusStyle } from "../helpers/status-style"
import { EntityIcon } from "../map/EntityIcon.tsx"
import type {
  EntityType,
  MapFeature,
  MapLayer
} from "../redux/global-map.types"

const { Text, Title } = Typography

interface EntityQuickViewEmptyStateProps {
  onSelectFavorite: (feature: {
    id: string
    entityType: MapLayer
    latitude: number
    longitude: number
  }) => void
  // "Look Around a Zip Code" — when the near-zip/radius filter is active,
  // this panel shows what's actually inside that radius instead of the
  // caller's favorites (favorites are a standing, location-independent
  // list; the near-radius filter is what the user is looking at RIGHT NOW,
  // so it wins). `features` is handed down already-fetched from the SAME
  // response the map itself renders (`MapTab`'s `filteredData`) — no
  // separate API call, and it stays in sync with the Lead/Project layer
  // toggle for free. `null`/`undefined` means "no near filter active",
  // which falls through to the favorites fetch below exactly as before.
  //
  // `totalCount` is the TRUE match count (`project_total + lead_total`,
  // entities/filter-aware, viewport-independent) — distinct from
  // `features.length`, which is 0 whenever the backend answers in
  // `mode: "clusters"` (300+ matches; `GLOBAL_MAP_RAW_FEATURE_THRESHOLD`)
  // even though real records exist. Without this split, a dense radius on a
  // large tenant would render this panel as if the radius were genuinely
  // empty — the exact same misleading-blank-panel bug this feature exists
  // to fix for `MapNoData`, just one level deeper.
  nearRadius?: {
    zip: string
    radiusMi: number
    features: MapFeature[]
    totalCount: number
  } | null
}

/**
 * What the details column shows while nothing is selected — the column
 * itself is always present (per the client's own before/after reference,
 * `Target Implementation.png`), never removed in favor of a full-width map,
 * so this fills the same panel dimensions `EntityQuickView` itself renders
 * into once a record is picked.
 *
 * Per explicit product direction, this "nothing selected" state is now the
 * caller's favorites list (star-toggled from `EntityQuickView`'s own
 * header) rather than a static placeholder message — clicking a row here
 * selects that record and recenters the map, the same behavior
 * `QuickViewNearby`'s own row click already has (`onSelectFavorite` mirrors
 * `onSelectFeature`'s shape exactly, so `MapTab.tsx` can route both through
 * closely related handlers). An empty favorites list renders the "select a
 * marker to view details" prompt — the column is a permanent part of the
 * content grid, so with nothing in it at all (the earlier behavior) it read
 * as an accidental blank rectangle rather than as a panel waiting for a
 * selection. This component fetches fresh every time it mounts,
 * which happens on every transition from a selected record back to none
 * (deselecting/closing), so a star toggled moments ago is already reflected
 * the next time this panel shows.
 */
const EntityQuickViewEmptyState = ({
  onSelectFavorite,
  nearRadius
}: EntityQuickViewEmptyStateProps) => {
  const { t } = useTranslation()
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading")
  const [features, setFeatures] = useState<MapFeature[]>([])

  useEffect(() => {
    // While the near-radius filter is active, its features (handed down as
    // a prop from the current map data) replace the favorites fetch
    // entirely — see this component's own doc comment above for why.
    if (nearRadius) return

    let cancelled = false
    setStatus("loading")
    void getFavorites().then((response) => {
      if (cancelled) return
      if (!response.ok || !response.data) {
        setStatus("error")
        return
      }
      setFeatures(response.data.features)
      setStatus("ready")
    })
    return () => {
      cancelled = true
    }
  }, [nearRadius])

  if (nearRadius) {
    // No box, no icon, no message — literally nothing, per explicit
    // direction ("if no project/lead in the radius - show nothing"). The
    // parent grid column (`MapTab.tsx`, `360px` fixed) reserves its own
    // width regardless of this panel's content, so an empty result here
    // never shifts the map. Checked against the TRUE total, not
    // `features.length` — see the prop's own doc comment for why those two
    // can disagree.
    if (nearRadius.totalCount === 0) return null

    // The dense/clustered case: real matches exist, but the backend didn't
    // enumerate them individually. Never silently render as if the radius
    // were empty.
    if (nearRadius.features.length === 0) {
      return (
        <div
          className={cn(
            GLOBAL_MAP_SURFACE_CLASS,
            "flex h-full w-full flex-col items-center justify-center gap-1 p-4 text-center"
          )}
          data-testid="global-map-details-empty-state"
        >
          <Text type="secondary" className="text-sm">
            {t("globalMap.quickView.nearRadius.tooManyToList", {
              count: nearRadius.totalCount,
              radius: nearRadius.radiusMi,
              zip: nearRadius.zip
            })}
          </Text>
        </div>
      )
    }

    return (
      <div
        className={cn(
          GLOBAL_MAP_SURFACE_CLASS,
          "flex h-full w-full flex-col gap-2 overflow-y-auto p-4"
        )}
        data-testid="global-map-details-empty-state"
      >
        <Title level={5} className="!mb-0 text-sm font-semibold text-slate-800">
          {t("globalMap.quickView.nearRadius.title", {
            radius: nearRadius.radiusMi,
            zip: nearRadius.zip
          })}
        </Title>
        {renderFeatureRows(nearRadius.features, onSelectFavorite)}
      </div>
    )
  }

  // Still fetching: an empty surface rather than a flash of the "select a
  // marker" prompt that a resolved, non-empty favorites list would replace a
  // moment later.
  if (status === "loading") {
    return (
      <div
        className={cn(GLOBAL_MAP_SURFACE_CLASS, "h-full w-full")}
        data-testid="global-map-details-empty-state"
      />
    )
  }

  if (status === "error") {
    return (
      <div
        className={cn(
          GLOBAL_MAP_SURFACE_CLASS,
          "flex h-full w-full flex-col items-center justify-center gap-2 p-6 text-center"
        )}
        data-testid="global-map-details-empty-state"
      >
        <Text type="secondary" className="text-sm">
          {t("globalMap.quickView.favorites.loadError")}
        </Text>
      </div>
    )
  }

  // Nothing selected and nothing favorited — the column is a permanent part
  // of the row's layout, so with no content of its own it used to read as an
  // accidental blank rectangle. A deliberate prompt instead, naming the
  // gesture that fills the panel.
  if (features.length === 0) {
    return (
      <div
        className={cn(
          GLOBAL_MAP_SURFACE_CLASS,
          "flex h-full w-full flex-col items-center justify-center gap-3 px-6 py-10 text-center"
        )}
        data-testid="global-map-details-empty-state"
      >
        <MapIllustration width={190} />
        <div className="max-w-[280px]">
          <div className="text-[18px] font-semibold text-[#0F172A]">
            {t("globalMap.quickView.noSelection.title")}
          </div>
          <div className="mt-2 text-[13px] leading-relaxed text-slate-500">
            {t("globalMap.quickView.noSelection.description")}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        GLOBAL_MAP_SURFACE_CLASS,
        "flex h-full w-full flex-col gap-2 overflow-y-auto p-4"
      )}
      data-testid="global-map-details-empty-state"
    >
      <Title level={5} className="!mb-0 text-sm font-semibold text-slate-800">
        {t("globalMap.quickView.favorites.title")}
      </Title>
      {renderFeatureRows(features, onSelectFavorite)}
    </div>
  )
}

function renderFeatureRows(
  features: MapFeature[],
  onSelect: EntityQuickViewEmptyStateProps["onSelectFavorite"]
) {
  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0">
      {features.map((feature) => (
        <li key={feature.id}>
          <button
            type="button"
            onClick={() =>
              onSelect({
                id: feature.id,
                entityType: feature.entity_type,
                latitude: feature.latitude,
                longitude: feature.longitude
              })
            }
            className="flex w-full min-w-0 flex-col gap-0.5 rounded px-1 py-1 text-left hover:bg-gray-50"
          >
            <span className="flex min-w-0 items-center gap-2">
              <EntityIcon entityType={feature.entity_type} className="size-6" />
              <Text
                ellipsis={{ tooltip: feature.title }}
                className="min-w-0 flex-1 text-sm"
              >
                {feature.title}
              </Text>
              <span
                className="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium"
                style={globalMapStatusStyle(
                  feature.entity_type,
                  feature.status_label
                )}
              >
                {feature.status_label}
              </span>
            </span>
            <span className="pl-8 text-xs text-slate-400">
              {feature.customer_name ?? "—"}
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}

export default EntityQuickViewEmptyState
